// MEO landmark ads (Phase 1, Step 4-3 PR 2, 2026-05-01):
// landmark の create / edit を担う Sheet (slide-over) UI。
// Mapbox 座標 picker は将来 PR 3 で追加、本 PR は座標を手入力。
//
// auth: BUSINESS は自分が sponsor の landmark のみ編集可 (api 側 enforced)。
// CMS 側は sponsor_user_id 入力欄を出すが、BUSINESS の値は api が自動上書きする。

import { Button } from '@/components/common/Button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/common/Form';
import { Input } from '@/components/common/Input';
import MapPreview from '@/components/common/MapPreview';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '@/components/common/Select';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetFooter,
    SheetHeader,
    SheetTitle
} from '@/components/common/Sheet';
import { Switch } from '@/components/common/Switch';
import {
    CouponTemplatesSection,
    ImagesSection,
    MeoEntrySection,
    MenuItemsSection,
    NeighborhoodsSection,
    OpeningHoursSection,
    ReviewBonusStrategySection,
    ShopInfoSection,
    SnsLinksSection
} from './MeoSections';
import FacilityQrSection from './FacilityQrSection';
import CardBackgroundField from './CardBackgroundField';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { getCurrentRoleId } from '@/utils/routes';
import { toastify } from '@/utils/toastify';
import { yupResolver } from '@hookform/resolvers/yup';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';

export const LANDMARK_CATEGORY_OPTIONS = [
    { value: 'currency_exchange', label: '両替機' },
    { value: 'shrine', label: '神社' },
    { value: 'tourist', label: '観光地' },
    { value: 'shop', label: '店舗' },
    { value: 'other', label: 'その他' }
];

// T1-A MEO sponsor 階層 (2026-05-04 PR 4、design/seo-landmark-spec.md §1.3.2 参照)
export const SPONSOR_TIER_OPTIONS = [
    { value: 'none', label: 'なし (= 既存 landmark、SEO 公開なし)' },
    { value: 'standard', label: 'スタンダード (¥10,000/月)' },
    { value: 'premium', label: 'プレミアム (¥25,000/月)' },
    { value: 'enterprise', label: 'エンタープライズ (¥50,000/月)' }
];

// Schema.org LocalBusiness sub-type 候補。design/seo-landmark-spec.md §1.2 参照。
// none は dropdown の sentinel (Radix Select は '' 値禁止)。
// label は日本語のみ表示 (内部値は Schema.org の英語 enum を保持)。
export const BUSINESS_TYPE_OPTIONS = [
    { value: '', label: '(未設定 / 自動選択)' },
    { value: 'Restaurant', label: 'レストラン' },
    { value: 'CafeOrCoffeeShop', label: 'カフェ' },
    { value: 'BarOrPub', label: 'バー / 居酒屋' },
    { value: 'FastFoodRestaurant', label: 'ファーストフード' },
    { value: 'Bakery', label: 'ベーカリー' },
    { value: 'Store', label: '小売店' },
    { value: 'ClothingStore', label: '衣料品店' },
    { value: 'ConvenienceStore', label: 'コンビニ' },
    { value: 'TouristInformationCenter', label: '観光案内所' }
];

// tier 別の写真 / メニュー上限。design/seo-landmark-spec.md §1.3.2 参照。
export const IMAGE_LIMIT_BY_TIER: Record<string, number> = {
    none: 0,
    standard: 1,
    premium: 5,
    enterprise: 5
};
export const MENU_LIMIT_BY_TIER: Record<string, number> = {
    none: 0,
    standard: 10,
    premium: 10,
    enterprise: 10
};

// area_slug の許容文字 (server 側 src/helpers/seoHelpers.js:isValidAreaSlug と同期)
const AREA_SLUG_REGEX = /^[a-z0-9][a-z0-9-]{0,118}[a-z0-9]$|^[a-z0-9]$/i;

// 2026-05-01: 新規追加時に位置情報が未取得 / 拒否された場合の fallback。
// PoC は沖縄本島南部運用のため那覇市中心付近を採用。geolocation 成功時はこの値は使われない。
const FALLBACK_LAT = 26.2125;
const FALLBACK_LNG = 127.6792;
const FALLBACK_ZOOM = 11;
// geolocation 取得 / edit mode で既に座標が確定している場合の zoom (街レベル)
const PIN_ZOOM = 13;
// geolocation 取得待ちの zoom (zoom 1 = 世界全体だと意味が無いので fallback 中心 + zoom 6 (= 地方レベル) で待機)
const LOADING_ZOOM = 6;

const landmarkSchema = yup.object({
    name: yup
        .string()
        .required('名称を入力してください')
        .max(255, '255 文字以下を入力してください'),
    category: yup
        .string()
        .required('カテゴリを選択してください')
        .oneOf(
            LANDMARK_CATEGORY_OPTIONS.map(o => o.value),
            'カテゴリは選択肢から選んでください'
        ),
    latitude: yup
        .number()
        .transform((v, o) => (o === '' ? undefined : v))
        .typeError('数値を入力してください')
        .required('緯度を入力してください')
        .min(-90, '-90〜90 の範囲で入力してください')
        .max(90, '-90〜90 の範囲で入力してください'),
    longitude: yup
        .number()
        .transform((v, o) => (o === '' ? undefined : v))
        .typeError('数値を入力してください')
        .required('経度を入力してください')
        .min(-180, '-180〜180 の範囲で入力してください')
        .max(180, '-180〜180 の範囲で入力してください'),
    radiusM: yup
        .number()
        .transform((v, o) => (o === '' ? undefined : v))
        .typeError('数値を入力してください')
        .integer('整数で入力してください')
        .min(1, '1 以上を入力してください')
        .max(100000, '100000 以下を入力してください')
        .required('半径を入力してください'),
    sponsorUserId: yup
        .number()
        .transform((v, o) => (o === '' || o == null ? null : v))
        .nullable()
        .integer('整数で入力してください')
        .min(1, '1 以上を入力してください'),
    // T3-B PR 4-bis (2026-05-12): omikuji ガチャフレームワーク のテンプレートセット紐付け。
    // 未指定 = system default ('currency-exchange-poc' = 両替機 PoC 5 種) を drawOmikuji が使う。
    templateSetId: yup
        .number()
        .transform((v, o) => (o === '' || o == null ? null : v))
        .nullable()
        .integer('整数で入力してください')
        .min(1, '1 以上を入力してください'),

    // P4 #20 (2026-07-07): クーポン専用モード。true でこの landmark の draw は
    // 占い要素 (運勢ラベル + 占い文) を出さずクーポンだけ表示する。
    // Spec: design/coupon-only-mode-spec-20260707.md §3.4
    couponOnly: yup.boolean().default(false),
    // デモ用のスポット (2026-09-28 §47)。ON で卓上 QR / 宿 QR の画面の最上部に「デモ用です」の帯を出す。管理者のみ。
    isDemo: yup.boolean().default(false),

    // 店ごとのカード背景画像 (2026-09-26)。アップロード済みの配信 URL。null = 従来の絵柄。
    // Spec: design/landmark-card-background-spec.md
    cardBackgroundUrl: yup.string().nullable().default(null),

    // T1-A MEO 入稿フィールド群 (2026-05-04 PR 4)
    // sponsorTier='none' の場合 MEO ページは noindex 扱い。詳細 spec 参照。
    sponsorTier: yup
        .string()
        .required('スポンサー階層を選択してください')
        .oneOf(
            SPONSOR_TIER_OPTIONS.map(o => o.value),
            'スポンサー階層は選択肢から選んでください'
        ),
    sponsorStartedAt: yup
        .string()
        .nullable()
        .transform((v, o) => (o === '' ? null : v)),
    sponsorPaidUntil: yup
        .string()
        .nullable()
        .transform((v, o) => (o === '' ? null : v)),
    areaSlug: yup
        .string()
        .nullable()
        .transform((v, o) => (o === '' ? null : v))
        .max(120, '120 文字以下を入力してください')
        .test(
            'area-slug-format',
            '英数字とハイフンのみ使用できます (例: naha-kokusaidori)',
            v => v == null || v === '' || AREA_SLUG_REGEX.test(v)
        ),

    // 店舗情報 (sponsorTier ≠ none で意味を持つ、UI で hidden 制御)
    addressStreet: yup.string().nullable().max(255).transform((v, o) => (o === '' ? null : v)),
    addressCity: yup.string().nullable().max(120).transform((v, o) => (o === '' ? null : v)),
    addressRegion: yup.string().nullable().max(120).transform((v, o) => (o === '' ? null : v)),
    postalCode: yup.string().nullable().max(20).transform((v, o) => (o === '' ? null : v)),
    phone: yup.string().nullable().max(40).transform((v, o) => (o === '' ? null : v)),
    businessType: yup.string().nullable().transform((v, o) => (o === '' ? null : v)),
    description: yup
        .string()
        .nullable()
        .max(200, '200 文字以下を入力してください')
        .transform((v, o) => (o === '' ? null : v)),
    seoDescription: yup
        .string()
        .nullable()
        .max(160, '160 文字以下を入力してください (SEO meta description 上限)')
        .transform((v, o) => (o === '' ? null : v)),

    // 営業時間: JSONB スキーマを直 JSON テキスト入力で受ける (= MVP)
    // 例: [{"dayOfWeek":"Monday","opens":"09:00","closes":"18:00"}]
    openingHoursJson: yup
        .string()
        .nullable()
        .transform((v, o) => (o === '' ? null : v))
        .test('valid-json', '正しい JSON 配列を入力してください', (v) => {
            if (v == null || v === '') return true;
            try {
                const parsed = JSON.parse(v);
                if (!Array.isArray(parsed)) return false;
                return parsed.every((e: any) =>
                    e &&
                    typeof e === 'object' &&
                    (typeof e.dayOfWeek === 'string' || Array.isArray(e.dayOfWeek)) &&
                    typeof e.opens === 'string' &&
                    typeof e.closes === 'string');
            } catch {
                return false;
            }
        }),

    // SNS / 公式リンク群 (= metadata.sns.{instagram,twitter,facebook,website})
    snsInstagram: yup.string().nullable().url('URL を入力してください').transform((v, o) => (o === '' ? null : v)),
    snsTwitter: yup.string().nullable().url('URL を入力してください').transform((v, o) => (o === '' ? null : v)),
    snsFacebook: yup.string().nullable().url('URL を入力してください').transform((v, o) => (o === '' ? null : v)),
    snsWebsite: yup.string().nullable().url('URL を入力してください').transform((v, o) => (o === '' ? null : v)),

    // 写真 URL 配列 (= metadata.images)。tier 別上限は UI 側でガード。
    images: yup.array().of(
        yup.object({
            url: yup.string().required('URL を入力してください').max(2048)
        })
    ),

    // メニュー / 商品配列 (= metadata.menu_items)。tier 別上限は UI 側でガード。
    menuItems: yup.array().of(
        yup.object({
            name: yup.string().required('名前を入力してください').max(120),
            price: yup.string().nullable().max(40).transform((v, o) => (o === '' ? null : v)),
            description: yup.string().nullable().max(200).transform((v, o) => (o === '' ? null : v)),
            image_url: yup.string().nullable().max(2048).transform((v, o) => (o === '' ? null : v))
        })
    )
    // P1-4 (2026-07-08): 旧 coupon_by_fortune 入稿フィールドは撤去
    // (入力 UI は Phase 2 PR 6 で撤去済、api の旧 path も撤去済 = coupon-redesign PR 8)。
});

interface LandmarkFormSheetProps {
    open: boolean,
    onOpenChange: (open: boolean) => void,
    mode: 'create' | 'edit',
    landmark?: any | null,
    onSuccess?: () => void
}

export default observer(function LandmarkFormSheet({
    open,
    onOpenChange,
    mode,
    landmark,
    onSuccess
}: LandmarkFormSheetProps) {
    const {
        landmarkStore: { create, update },
        tierCatalogStore,
        userStore: { businessUsers, loadBusinessUsers }
    } = useStore();

    // ADMIN = 0。BUSINESS には ADMIN 専用 API を呼ぶ欄 (テンプレートセット等) を出さない。
    const isAdmin = getCurrentRoleId() === 0;

    const form = useForm({
        resolver: yupResolver(landmarkSchema),
        mode: 'onSubmit',
        defaultValues: {
            name: '',
            category: 'currency_exchange',
            latitude: FALLBACK_LAT,
            longitude: FALLBACK_LNG,
            radiusM: 30,
            sponsorUserId: null,
            // PR 4-bis (2026-05-12) default: null = system default template_set
            templateSetId: null,
            // P4 #20 (2026-07-07): クーポン専用モード default off = 現行挙動
            couponOnly: false,
            isDemo: false,
            cardBackgroundUrl: null,
            // T1-A MEO defaults (2026-05-04)
            sponsorTier: 'none',
            sponsorStartedAt: null,
            sponsorPaidUntil: null,
            areaSlug: null,
            addressStreet: null,
            addressCity: null,
            addressRegion: null,
            postalCode: null,
            phone: null,
            businessType: null,
            description: null,
            seoDescription: null,
            openingHoursJson: null,
            snsInstagram: null,
            snsTwitter: null,
            snsFacebook: null,
            snsWebsite: null,
            images: [],
            menuItems: []
        } as any
    });

    // useFieldArray は form 構築直後に登録する必要がある。
    const imagesFieldArray = useFieldArray({ control: form.control, name: 'images' as any });
    const menuItemsFieldArray = useFieldArray({ control: form.control, name: 'menuItems' as any });

    // tier 変更時に upper-limit を超える場合 UI 側で警告 (= submit ガードは onSubmit 側でも実施)。
    const sponsorTierWatched = form.watch('sponsorTier' as any) || 'none';
    const isMeoTier = sponsorTierWatched !== 'none';
    const imageLimit = useMemo(() => IMAGE_LIMIT_BY_TIER[sponsorTierWatched as string] ?? 0, [sponsorTierWatched]);
    const menuLimit = useMemo(() => MENU_LIMIT_BY_TIER[sponsorTierWatched as string] ?? 0, [sponsorTierWatched]);

    // 2026-05-01: 新規追加時の位置情報取得状態
    // 'idle' = 未試行 (edit mode / sheet 閉じ時)
    // 'pending' = 取得中 (zoom を緩めて表示)
    // 'success' = 取得済み (lat/lng は geolocation 値、zoom 街レベル)
    // 'fallback' = 拒否/失敗 (fallback 座標で zoom 緩め)
    const [geoState, setGeoState] = useState<'idle' | 'pending' | 'success' | 'fallback'>('idle');
    // sheet 閉じ→再オープン中に古い geolocation callback が遅れて発火する race を遮断するためのフラグ。
    // open 切り替えごとに新しい token を発行し、callback はマッチした時のみ state を更新する。
    const geoTokenRef = useRef(0);

    // Reset form when sheet opens with different landmark / mode.
    useEffect(() => {
        if (!open) {
            setGeoState('idle');
            geoTokenRef.current += 1; // 進行中の callback を無効化
            return;
        }
        // PR 4-bis (2026-05-12): template_set dropdown 用 load (open 時のみ、初回 only も possible)
        // 2026-09-26: 一覧 API (fortune-tier-presets / motif-template-sets / template-motifs) は ADMIN 専用で、
        // BUSINESS が呼ぶと 403 → CMS が強制 logout していた (= 店がスポット編集を開けない)。
        // loadBusinessUsers と同じく ADMIN の時だけ読む (テンプレートセット欄も ADMIN のみ表示)。
        if (isAdmin && !tierCatalogStore.loaded) {
            flowResult(tierCatalogStore.loadAll());
        }
        // スポンサー事業者 dropdown 用に BUSINESS user 一覧を load (open ごと、最新反映のため)。
        // ⚠️ GET /system/user は ADMIN 専用 + CMS は 403 で強制 logout (ApiStore.ts:80) のため、
        // BUSINESS ログイン時は呼ばない (= 2026-07-07 退行 fix: #116 で BUSINESS が form を開くと logout していた)。
        if (getCurrentRoleId() === 0) {
            flowResult(loadBusinessUsers());
        }
        if (mode === 'edit' && landmark) {
            const md = landmark.metadata || {};
            // ISO 8601 → input[type=date] が読める yyyy-mm-dd へ変換
            const toDateInput = (v: any) => {
                if (!v) return null;
                const d = new Date(v);
                if (Number.isNaN(d.getTime())) return null;
                return d.toISOString().slice(0, 10);
            };
            form.reset({
                name: landmark.name ?? '',
                category: landmark.category ?? 'currency_exchange',
                latitude: Number(landmark.latitude) || FALLBACK_LAT,
                longitude: Number(landmark.longitude) || FALLBACK_LNG,
                radiusM: landmark.radius_m ?? 30,
                sponsorUserId: landmark.sponsor_user_id ?? null,
                // PR 4-bis (2026-05-12)
                templateSetId: landmark.template_set_id ?? null,
                // P4 #20 (2026-07-07): クーポン専用モードの既存値読み出し
                couponOnly: !!landmark.coupon_only,
                isDemo: !!landmark.is_demo,
                cardBackgroundUrl: landmark.card_background_url ?? null,
                // T1-A MEO 既存データの読み出し (2026-05-04)
                sponsorTier: landmark.sponsor_tier ?? 'none',
                sponsorStartedAt: toDateInput(landmark.sponsor_started_at),
                sponsorPaidUntil: toDateInput(landmark.sponsor_paid_until),
                areaSlug: landmark.area_slug ?? null,
                addressStreet: md.address_street ?? null,
                addressCity: md.address_city ?? null,
                addressRegion: md.address_region ?? null,
                postalCode: md.postal_code ?? null,
                phone: md.phone ?? null,
                businessType: md.business_type ?? null,
                description: md.description ?? null,
                seoDescription: md.seo_description ?? null,
                openingHoursJson: md.opening_hours_spec ? JSON.stringify(md.opening_hours_spec, null, 2) : null,
                snsInstagram: md.sns?.instagram ?? null,
                snsTwitter: md.sns?.twitter ?? null,
                snsFacebook: md.sns?.facebook ?? null,
                snsWebsite: md.sns?.website ?? null,
                images: Array.isArray(md.images) ? md.images.map((url: string) => ({ url })) : [],
                menuItems: Array.isArray(md.menu_items) ? md.menu_items : []
            } as any);
            setGeoState('success'); // edit は座標確定扱い (zoom 街レベル)
        } else {
            // 新規追加: 一旦 fallback で reset、geolocation 試行 → 成功なら setValue で上書き
            form.reset({
                name: '',
                category: 'currency_exchange',
                latitude: FALLBACK_LAT,
                longitude: FALLBACK_LNG,
                radiusM: 30,
                sponsorUserId: null,
                templateSetId: null,
                // P4 #20 (2026-07-07): 新規は off = 既存挙動互換
                couponOnly: false,
                isDemo: false,
                cardBackgroundUrl: null,
                // T1-A MEO defaults (新規は none = 既存挙動互換)
                sponsorTier: 'none',
                sponsorStartedAt: null,
                sponsorPaidUntil: null,
                areaSlug: null,
                addressStreet: null,
                addressCity: null,
                addressRegion: null,
                postalCode: null,
                phone: null,
                businessType: null,
                description: null,
                seoDescription: null,
                openingHoursJson: null,
                snsInstagram: null,
                snsTwitter: null,
                snsFacebook: null,
                snsWebsite: null,
                images: [],
                menuItems: []
            } as any);

            if (typeof navigator !== 'undefined' && navigator.geolocation) {
                geoTokenRef.current += 1;
                const myToken = geoTokenRef.current;
                setGeoState('pending');
                navigator.geolocation.getCurrentPosition(
                    (pos) => {
                        if (geoTokenRef.current !== myToken) return; // stale
                        form.setValue('latitude', pos.coords.latitude);
                        form.setValue('longitude', pos.coords.longitude);
                        setGeoState('success');
                    },
                    () => {
                        if (geoTokenRef.current !== myToken) return; // stale
                        setGeoState('fallback');
                    },
                    { enableHighAccuracy: false, timeout: 5000, maximumAge: 60000 }
                );
            } else {
                setGeoState('fallback');
            }
        }
    }, [open, mode, landmark]);

    const onSubmit = async (data: any) => {
        const payload: any = {
            name: data.name,
            category: data.category,
            latitude: Number(data.latitude),
            longitude: Number(data.longitude),
            radius_m: Number(data.radiusM)
        };
        // sponsor_user_id: ADMIN は任意指定可、BUSINESS は api 側で自分にハードセット
        if (data.sponsorUserId != null && data.sponsorUserId !== '') {
            payload.sponsor_user_id = Number(data.sponsorUserId);
        }
        // PR 4-bis (2026-05-12): template_set_id (= omikuji ガチャフレームワーク 動的解決の起点)
        // null / 未指定 → backend で system default (= currency-exchange-poc) fallback。
        payload.template_set_id = (data.templateSetId != null && data.templateSetId !== '') ?
            Number(data.templateSetId) :
            null;

        // P4 #20 (2026-07-07): クーポン専用モード。always 送信 (false も明示)。
        payload.coupon_only = !!data.couponOnly;
        // §47: デモ用の印は管理者のみ (API も ADMIN 以外は無視する)。
        if (isAdmin) payload.is_demo = !!data.isDemo;
        // 店ごとのカード背景画像 (2026-09-26)。always 送信 (null = 削除 / 未設定)。
        payload.card_background_url = data.cardBackgroundUrl || null;

        // T1-A MEO 入稿 (2026-05-04)
        // sponsor_tier は always 送信 (none も含めて backend に明示)
        payload.sponsor_tier = data.sponsorTier || 'none';
        // 日付 input (yyyy-mm-dd) → ISO 8601 (= +09:00 で送る、サーバー側で TIMESTAMPTZ に変換)
        const toIsoTz = (s: string | null | undefined) =>
            s ? new Date(`${s}T00:00:00+09:00`).toISOString() : null;
        payload.sponsor_started_at = toIsoTz(data.sponsorStartedAt);
        payload.sponsor_paid_until = toIsoTz(data.sponsorPaidUntil);
        payload.area_slug = data.areaSlug || null;

        // metadata: 既存 landmark.metadata がある場合の coupon_by_fortune 等を保持しつつ拡張。
        const baseMetadata = (mode === 'edit' && landmark?.metadata) ? { ...landmark.metadata } : {};
        const metadata: any = { ...baseMetadata };
        const setOrDelete = (key: string, value: any) => {
            if (value == null || value === '') {
                delete metadata[key];
            } else {
                metadata[key] = value;
            }
        };
        setOrDelete('address_street', data.addressStreet);
        setOrDelete('address_city', data.addressCity);
        setOrDelete('address_region', data.addressRegion);
        setOrDelete('postal_code', data.postalCode);
        setOrDelete('phone', data.phone);
        setOrDelete('business_type', data.businessType);
        setOrDelete('description', data.description);
        setOrDelete('seo_description', data.seoDescription);

        // 営業時間 JSON parse (yup 側で既に validate 済)
        if (data.openingHoursJson) {
            try {
                metadata.opening_hours_spec = JSON.parse(data.openingHoursJson);
            } catch {
                // 万一 yup test を抜けた場合は未設定扱い
                delete metadata.opening_hours_spec;
            }
        } else {
            delete metadata.opening_hours_spec;
        }

        // SNS は object に集約 (= サーバ側 metadata.sns で参照)
        const sns: any = {};
        if (data.snsInstagram) sns.instagram = data.snsInstagram;
        if (data.snsTwitter) sns.twitter = data.snsTwitter;
        if (data.snsFacebook) sns.facebook = data.snsFacebook;
        if (data.snsWebsite) sns.website = data.snsWebsite;
        if (Object.keys(sns).length > 0) {
            metadata.sns = sns;
        } else {
            delete metadata.sns;
        }

        // 画像 array (= URL string array)
        const images = (data.images ?? []).map((i: any) => i?.url).filter(Boolean);
        if (images.length > 0) {
            metadata.images = images;
        } else {
            delete metadata.images;
        }

        // メニュー array
        const menuItems = (data.menuItems ?? [])
            .map((m: any) => ({
                name: m?.name,
                price: m?.price || undefined,
                description: m?.description || undefined,
                image_url: m?.image_url || undefined
            }))
            .filter((m: any) => m.name); // name が空の row は除外
        if (menuItems.length > 0) {
            metadata.menu_items = menuItems;
        } else {
            delete metadata.menu_items;
        }

        // P1-4 (2026-07-08): 旧 coupon_by_fortune は撤去済 (api の drawOmikuji 旧 path 撤去 +
        // migration §38 でキー掃除)。CMS からは今後書き込まないため、既存 metadata に残っていても
        // 保存時に確実に落とす (baseMetadata spread 由来の残留防止)。
        delete metadata.coupon_by_fortune;

        payload.metadata = Object.keys(metadata).length > 0 ? metadata : null;

        let res;
        if (mode === 'edit' && landmark?.id) {
            res = await flowResult(update({ id: landmark.id, data: payload }));
        } else {
            res = await flowResult(create(payload));
        }

        if (res?.ok) {
            toastify('success', mode === 'edit' ? '更新しました' : '作成しました');
            onOpenChange(false);
            onSuccess?.();
        } else {
            toastify('error', res?.message || '保存に失敗しました');
        }
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            {/* 2026-05-01: 小型 PC (高さ 700px 程度) でも footer (キャンセル/作成) が見切れないよう、
                SheetContent を flex-col にし、本体を flex-1 overflow-y-auto、footer を shrink-0 で固定。
                2026-05-05: Sheet variant の sm:max-w-sm (= 384px cap) を上書きして、画面サイズに応じ
                560 / 720 / 880px へ拡張。MEO 入稿フィールドが多いため、入力しやすさを優先。 */}
            <SheetContent
                className='w-full max-w-full sm:max-w-[560px] lg:max-w-[720px] xl:max-w-[880px] flex flex-col p-0'
                side='right'
            >
                <SheetHeader className='shrink-0 px-6 pt-6 pb-4 border-b'>
                    <SheetTitle>
                        {mode === 'edit' ? 'ランドマーク編集' : 'ランドマーク新規追加'}
                    </SheetTitle>
                    <SheetDescription>
                        {mode === 'edit' ?
                            `ID: ${landmark?.id ?? '-'} の項目を編集します。` :
                            '両替機等の物理点を新規登録します。位置情報を取得して仮配置します。ピンをドラッグするか緯度・経度を直接入力してください。'}
                    </SheetDescription>
                </SheetHeader>

                <Form {...form}>
                    <form
                        onSubmit={form.handleSubmit(onSubmit)}
                        className='flex flex-col flex-1 min-h-0'
                    >
                        <div className='flex-1 overflow-y-auto px-6 py-4 space-y-4'>
                            <FormField
                                control={form.control}
                                name='name'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>名称 *</FormLabel>
                                        <FormControl>
                                            <Input placeholder='例: 那覇空港 両替機 1 号' {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name='category'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>カテゴリ *</FormLabel>
                                        <FormControl>
                                            <Select
                                                onValueChange={field.onChange}
                                                value={typeof field.value === 'string' ? field.value : 'currency_exchange'}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {LANDMARK_CATEGORY_OPTIONS.map(opt => (
                                                        <SelectItem
                                                            key={opt.value}
                                                            value={opt.value}
                                                            className='hover:cursor-pointer'
                                                        >
                                                            {opt.label}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <div className='grid grid-cols-2 gap-3'>
                                <FormField
                                    control={form.control}
                                    name='latitude'
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>緯度 *</FormLabel>
                                            <FormControl>
                                                <Input
                                                    type='number'
                                                    step='0.000001'
                                                    placeholder='例: 26.196'
                                                    {...field}
                                                    value={field.value ?? ''}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name='longitude'
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>経度 *</FormLabel>
                                            <FormControl>
                                                <Input
                                                    type='number'
                                                    step='0.000001'
                                                    placeholder='例: 127.700'
                                                    {...field}
                                                    value={field.value ?? ''}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>

                            {/* MEO landmark ads (Phase 1, 案 A foundation, 2026-05-01):
                            inline preview + draggable picker。
                            geolocation 取得中 / 失敗時の zoom を地方レベルにして「何処から始めるか」を伝わりやすく。 */}
                            {(() => {
                                const lat = Number(form.watch('latitude'));
                                const lng = Number(form.watch('longitude'));
                                const safeLat = isFinite(lat) ? lat : FALLBACK_LAT;
                                const safeLng = isFinite(lng) ? lng : FALLBACK_LNG;
                                const previewZoom =
                                geoState === 'pending' ?
                                    LOADING_ZOOM :
                                    geoState === 'fallback' ?
                                        FALLBACK_ZOOM :
                                        PIN_ZOOM;
                                const hint =
                                geoState === 'pending' ?
                                    '位置情報を取得中…' :
                                    geoState === 'fallback' ?
                                        '位置情報の取得に失敗しました (沖縄を仮表示)。ピンをドラッグするか緯度・経度を直接入力してください。' :
                                        'ピンをドラッグして微調整できます';
                                return (
                                    <div>
                                        <FormLabel>配置プレビュー</FormLabel>
                                        <p className='text-xs text-muted-foreground mt-1'>{hint}</p>
                                        <div className='mt-2'>
                                            <MapPreview
                                                latitude={safeLat}
                                                longitude={safeLng}
                                                draggable
                                                zoom={previewZoom}
                                                onMove={(nLat, nLng) => {
                                                    form.setValue('latitude', nLat);
                                                    form.setValue('longitude', nLng);
                                                    if (geoState !== 'success') setGeoState('success');
                                                }}
                                            />
                                        </div>
                                    </div>
                                );
                            })()}

                            <FormField
                                control={form.control}
                                name='radiusM'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>半径 (m) *</FormLabel>
                                        <FormControl>
                                            <Input
                                                type='number'
                                                placeholder='例: 30 (両替機の前 30m 圏)'
                                                {...field}
                                                value={field.value ?? ''}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name='sponsorUserId'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>スポンサー事業者 (任意、管理者のみ指定可)</FormLabel>
                                        <p className='text-xs text-muted-foreground'>
                                            この landmark を所有する事業者 (BUSINESS ロール) を選択します。
                                            指定すると月次 SEO レポートが当該事業者の email に送られます。未選択 = 個人 landmark。
                                            候補は「ユーザー管理」で作成した事業者ユーザーです。
                                        </p>
                                        {getCurrentRoleId() === 0 ?
                                            (
                                                <FormControl>
                                                    <Select
                                                        value={field.value != null && field.value !== '' ? String(field.value) : '__none__'}
                                                        onValueChange={v => field.onChange(v === '__none__' ? null : Number(v))}
                                                    >
                                                        <SelectTrigger>
                                                            <SelectValue placeholder='選択してください' />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value='__none__'>未設定 (= 個人 landmark)</SelectItem>
                                                            {businessUsers.map(u => (
                                                                <SelectItem key={u.id} value={String(u.id)}>
                                                                    {u.username}
                                                                    {' '}
                                                                    (ID:
                                                                    {' '}
                                                                    {u.id}
                                                                    )
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </FormControl>
                                            ) :
                                            (
                                                // BUSINESS ログイン時: 事業者一覧 API (= ADMIN 専用) は呼べないため
                                                // 固定表示。値は api 側が自分の user.id に自動セットする。
                                                <p className='text-sm text-gray-600 border rounded-md px-3 py-2 bg-gray-50'>
                                                    自分の事業者アカウントが自動で設定されます (変更不可)
                                                </p>
                                            )}
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            {/* PR 4-bis (2026-05-12): omikuji ガチャフレームワーク のテンプレートセット紐付け。
                                未指定 = system default (currency-exchange-poc = 両替機 PoC) を drawOmikuji が使う。
                                Spec: design/sponsored-motif-spec.md v3 §4.4 step 5 / §7.1 */}
                            {isAdmin && (
                                <FormField
                                    control={form.control}
                                    name='templateSetId'
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>omikuji テンプレートセット (任意)</FormLabel>
                                            <p className='text-xs text-muted-foreground'>
                                                この landmark で omikuji を引いた時に使う tier プリセット + motif セット。
                                                空欄 = 既定の中立デザインのカード (地域特性なし、2026-09-26 〜。旧: 両替機 PoC の紅型)。
                                                {' '}
                                                ※ 現在は5段(おみくじ)プリセットのセットのみ割当可。非5段(ビンゴ/スタンプ等)は Phase 3 対応予定。
                                            </p>
                                            <FormControl>
                                                <Select
                                                    value={field.value != null ? String(field.value) : '__default__'}
                                                    onValueChange={v => field.onChange(v === '__default__' ? null : Number(v))}
                                                >
                                                    <SelectTrigger>
                                                        <SelectValue placeholder='選択してください' />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value='__default__'>既定 (中立デザイン)</SelectItem>
                                                        {/* P1 Phase 1 §3.6 ガード: 非5段プリセットのセットは disable (Phase 3 で解除)。
                                                        現在選択中の値は編集継続のため例外的に選択可のまま残す。 */}
                                                        {tierCatalogStore.templateSets
                                                            .filter(ts => ts.is_active)
                                                            .map((ts) => {
                                                                const tierCount = tierCatalogStore.getPresetById(ts.tier_preset_id)?.tier_definitions?.length ?? 0;
                                                                const isFiveTier = tierCount === 5;
                                                                const isCurrent = String(ts.id) === String(field.value ?? '');
                                                                return (
                                                                    <SelectItem
                                                                        key={ts.id}
                                                                        value={String(ts.id)}
                                                                        disabled={!isFiveTier && !isCurrent}
                                                                    >
                                                                        {ts.name}
                                                                        {' '}
                                                                        (
                                                                        {ts.slug}
                                                                        )
                                                                        {!isFiveTier && ` — ${tierCount}段 (Phase 3対応予定・割当不可)`}
                                                                    </SelectItem>
                                                                );
                                                            })}
                                                    </SelectContent>
                                                </Select>
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            )}

                            {/* P4 #20 (2026-07-07): クーポン専用モード トグル。
                                ON にすると、この landmark で omikuji を引いた時に運勢ラベル + 占い文を
                                出さず、クーポン (画像 / コード / 説明文) だけを表示する。
                                占い文言 (omikuji_texts) 未 seed の landmark でもクーポン配布機として使える。
                                Spec: design/coupon-only-mode-spec-20260707.md */}
                            <FormField
                                control={form.control}
                                name='couponOnly'
                                render={({ field }) => (
                                    <FormItem className='flex flex-row items-center justify-between rounded-md border px-3 py-3'>
                                        <div className='pr-3'>
                                            <FormLabel>クーポン専用モード</FormLabel>
                                            <p className='text-xs text-muted-foreground mt-1'>
                                                ON で運勢ラベル + 占い文を表示せず、クーポンだけを出します
                                                (占い要素なしのクーポン配布機として使う場合)。OFF = 通常のおみくじ表示。
                                            </p>
                                        </div>
                                        <FormControl>
                                            <Switch
                                                checked={!!field.value}
                                                onCheckedChange={field.onChange}
                                            />
                                        </FormControl>
                                    </FormItem>
                                )}
                            />

                            {/* デモ用のスポット (2026-09-28 §47)。管理者のみ。
                                Spec: design/aircle-demo-coupon-screen-spec.md §8-2 (5) */}
                            {isAdmin && (
                                <FormField
                                    control={form.control}
                                    name='isDemo'
                                    render={({ field }) => (
                                        <FormItem className='flex flex-row items-center justify-between rounded-md border px-3 py-3'>
                                            <div className='pr-3'>
                                                <FormLabel>デモ用</FormLabel>
                                                <p className='text-xs text-muted-foreground mt-1'>
                                                    ON で、卓上 QR / 宿 QR の画面の一番上に「デモ用です。実際には使えません」の帯を出します
                                                    (営業・協業のデモ用スポット向け)。
                                                </p>
                                            </div>
                                            <FormControl>
                                                <Switch
                                                    checked={!!field.value}
                                                    onCheckedChange={field.onChange}
                                                />
                                            </FormControl>
                                        </FormItem>
                                    )}
                                />
                            )}

                            {/* 店ごとのカード背景画像 (2026-09-26)。
                                Spec: design/landmark-card-background-spec.md */}
                            <FormField
                                control={form.control}
                                name='cardBackgroundUrl'
                                render={({ field }) => (
                                    <FormItem>
                                        <CardBackgroundField value={field.value} onChange={field.onChange} />
                                    </FormItem>
                                )}
                            />

                            {/* T1-A MEO 入稿 セクション群 (2026-05-04 PR 4)
                                design/seo-landmark-spec.md §1.3 / §5.1
                                sponsor_tier='none' でも sponsor_tier select / area_slug は表示。
                                それ以外の店舗情報 / SNS / メニュー / 写真 等は tier !== 'none' で hidden 制御。
                                Phase 2 PR 6 (2026-05-15): 旧 CouponByFortuneSection (= JSON 入稿 UI)
                                を撤去し、CouponTemplatesSection (= 新管理画面誘導 + 縮小一覧) に置換。
                                旧 landmark.metadata.coupon_by_fortune の物理撤去は Phase 2 PR 8。 */}
                            <MeoEntrySection form={form} />
                            <CouponTemplatesSection landmark={landmark} />

                            {isMeoTier && (
                                <>
                                    <ShopInfoSection form={form} />
                                    <OpeningHoursSection form={form} />
                                    <SnsLinksSection form={form} />
                                    <MenuItemsSection
                                        form={form}
                                        fieldArray={menuItemsFieldArray}
                                        limit={menuLimit}
                                    />
                                    <ImagesSection
                                        form={form}
                                        fieldArray={imagesFieldArray}
                                        limit={imageLimit}
                                    />
                                </>
                            )}

                            {/* MEO Phase 1 Sprint 2-F (2026-05-13):
                                レビューリワード戦略 + 周辺スポット管理。
                                edit mode (= landmark.id 存在) でのみ表示。
                                Spec: design/meo-landmark-ads-spec.md §13 / §14 */}
                            {mode === 'edit' && landmark?.id && (
                                <>
                                    <ReviewBonusStrategySection landmark={landmark} onSaved={onSuccess} />
                                    <NeighborhoodsSection landmark={landmark} />
                                    {/* 宿 QR (2026-09-25, 民泊 PoC Step 1): 部屋ごとの固定 QR 発行 */}
                                    <FacilityQrSection landmark={landmark} />
                                </>
                            )}

                        </div>

                        <SheetFooter className='shrink-0 px-6 py-4 border-t bg-background'>
                            <Button
                                type='button'
                                variant='outline'
                                onClick={() => onOpenChange(false)}
                            >
                                キャンセル
                            </Button>
                            <Button type='submit' variant='active'>
                                {mode === 'edit' ? '更新' : '作成'}
                            </Button>
                        </SheetFooter>
                    </form>
                </Form>
            </SheetContent>
        </Sheet>
    );
});
